import { useSnowplowContext } from "@simplybusiness/services";
import classNames from "classnames/dedupe";
import { useAtom } from "jotai";
import { ANNUAL, MONTHLY, YEARLY } from "../../../constants/constants";
import {
  usePaymentToggleState,
  useSetPaymentToggleAtom,
} from "../../../hooks/usePaymentToggleState";
import { useTogglePaymentPreference } from "../../../hooks/useTogglePaymentPreference";
import { sliderStateAtom } from "../../../store";
import type { PaymentPeriod } from "../../QuoteCards/types";
import { useLocale } from "../../../hooks/useLocale";

export type PaymentToggleProps = {
  paymentTogglePeriod?: PaymentPeriod;
  shouldRecordPreference?: boolean;
};

export const PaymentToggle = ({
  paymentTogglePeriod,
  shouldRecordPreference,
}: PaymentToggleProps) => {
  const { snowplow } = useSnowplowContext();
  useSetPaymentToggleAtom(paymentTogglePeriod);
  const [paymentPeriod, setPaymentPeriod] = usePaymentToggleState();
  const [sliderIsOpen] = useAtom(sliderStateAtom);
  const { togglePaymentPreference } = useTogglePaymentPreference();
  const { isUK } = useLocale();
  const isAnnual = paymentPeriod === ANNUAL;

  const handleClick = () => {
    const selectedPeriod = paymentPeriod === MONTHLY ? ANNUAL : MONTHLY;
    setPaymentPeriod(selectedPeriod);
    const category = `${
      sliderIsOpen ? "quote_details_slider" : "qcp"
    }_payment_toggle`;

    snowplow?.trigger("paymentToggleClicked", {
      category,
      label: isAnnual ? "monthly payments" : "one-time payment",
    });
    // Store preference on server
    if (shouldRecordPreference) {
      togglePaymentPreference(selectedPeriod);
    }
  };

  const buttonClasses = classNames("mobius-payment-toggle", {
    "--is-toggled": isAnnual,
  });

  return (
    <button
      type="button"
      className={buttonClasses}
      onClick={handleClick}
      role="switch"
      aria-checked={isAnnual}
      aria-label={`Switch to ${isAnnual ? MONTHLY : YEARLY} payment`}
      data-testid="payment-toggle"
    >
      <div
        className="mobius-payment-toggle__slider"
        role="presentation"
        aria-hidden="true"
      />
      <span
        className="mobius-payment-toggle__text --monthly"
        aria-hidden={isAnnual}
      >
        Monthly
      </span>
      <span
        className="mobius-payment-toggle__text --yearly"
        aria-hidden={!isAnnual}
      >
        {isUK ? "Annual" : "Yearly"}
      </span>
    </button>
  );
};
